import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Tag,
  Tabs,
  message,
  Tooltip,
} from "antd";
import {
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import axios from "axios";
import consts from "../../constant";
import Loadingpage from "../Loadingpage/Loadingpage";
import EditableTable from "../../Reusable/Table/EditableTable";

const DEFAULT_MULTIPLIER = "1.2";

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("Jetix")}`,
});

const sanitizeMultiplierInput = (value) => {
  const sanitizedValue = String(value || "")
    .replace(/[^\d.]/g, "")
    .replace(/(\..*)\./g, "$1");
  const [integerPart, decimalPart] = sanitizedValue.split(".");

  if (decimalPart === undefined) return integerPart;

  return `${integerPart}.${decimalPart.slice(0, 2)}`;
};

const multiplierRules = [
  { required: true, message: "Enter multiplier" },
  {
    pattern: /^\d+(\.\d{1,2})?$/,
    message: "Enter up to 2 decimal places",
  },
];

const formatDateTime = (date = new Date()) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);

const formatPatternDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : formatDateTime(date);
};

const getPatternId = (pattern) => pattern?._id || pattern?.id;

const normalizePatternValue = (value) => {
  const normalizedValue = String(value || "").trim().toLowerCase();

  if (normalizedValue === "u" || normalizedValue === "up") return "up";
  if (normalizedValue === "d" || normalizedValue === "down") return "down";

  return normalizedValue;
};

const toApiPatternValue = (value) => (value === "up" ? "U" : "D");
const toApiPattern = (sequence) => sequence.map(toApiPatternValue);

const normalizePatternSequence = (pattern) => {
  const value = pattern?.pattern || pattern?.sequence || [];

  if (Array.isArray(value)) return value.map(normalizePatternValue);
  if (typeof value === "string") {
    const values = value.includes(",") ? value.split(",") : value.split("");

    return values
      .map(normalizePatternValue)
      .filter(Boolean);
  }

  return [];
};

const normalizePatternStatus = (pattern) => {
  const value = pattern?.status ?? pattern?.isActive;

  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    return value.toLowerCase() === "true" || value.toLowerCase() === "active";
  }

  return Boolean(value);
};

const normalizePattern = (pattern) => ({
  ...pattern,
  id: getPatternId(pattern),
  key: getPatternId(pattern),
  sequence: normalizePatternSequence(pattern),
  multiplier: String(pattern?.multiplier ?? DEFAULT_MULTIPLIER),
  isActive: normalizePatternStatus(pattern),
  createdAt: formatPatternDate(pattern?.createdAt),
});

const extractPatterns = (data) => {
  const result = data?.result ?? data?.data ?? data;

  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.docs)) return result.docs;
  if (Array.isArray(result?.patterns)) return result.patterns;

  return [];
};

const extractPatternTotal = (data) => {
  const result = data?.result ?? data?.data ?? data;

  return (
    result?.totalDocs ??
    result?.total ??
    data?.totalDocs ??
    data?.total ??
    0
  );
};

const PatternBadge = ({ value }) => {
  if (!value) return <span className="trade-pattern-chip empty">-</span>;

  return (
    <span className={`trade-pattern-chip ${value}`}>
      {value === "up" ? "U" : "D"}
    </span>
  );
};

const TradePatternSettings = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [patterns, setPatterns] = useState([]);
  const [totalPatterns, setTotalPatterns] = useState(0);
  const [activeRole, setActiveRole] = useState("basic");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);
  const [deletingPattern, setDeletingPattern] = useState(null);
  const [isEditPatternChanged, setIsEditPatternChanged] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [page, setPage] = useState({
    current: 1,
    pageSize: 10,
  });

  const fetchPatterns = useCallback(
    async (
      role = activeRole,
      status = filterStatus,
      currentPage = page.current,
      pageSize = page.pageSize
    ) => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${consts.BackendUrl}/pattern/patterns`, {
          params: {
            role,
            status: status || undefined,
            page: currentPage,
            limit: pageSize,
          },
          headers: authHeaders(),
        });

        setTotalPatterns(extractPatternTotal(data));
        setPatterns(extractPatterns(data).map(normalizePattern));
      } catch (error) {
        messageApi.open({
          type: "error",
          content: error.response?.data?.message || "Failed to fetch patterns",
        });
      } finally {
        setLoading(false);
      }
    },
    [activeRole, filterStatus, messageApi, page.current, page.pageSize]
  );

  useEffect(() => {
    fetchPatterns(activeRole, filterStatus, page.current, page.pageSize);
  }, [activeRole, filterStatus, page.current, page.pageSize, fetchPatterns]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalPatterns / page.pageSize));

    if (page.current > maxPage) {
      setPage((prev) => ({
        ...prev,
        current: maxPage,
      }));
    }
  }, [totalPatterns, page.current, page.pageSize]);

  const getSequenceFromValues = (values) =>
    (values.sequence || []).filter(Boolean);

  const applyStatusFilter = (value) => {
    const nextStatus = value || "";
    setFilterStatus(nextStatus);
    setPage((prev) => ({ ...prev, current: 1 }));
  };

  const clearStatusFilter = () => {
    setFilterStatus("");
    setPage((prev) => ({ ...prev, current: 1 }));
  };

  const hasPatternChanged = (values, pattern) => {
    if (!pattern) return false;

    const currentSequence = (values.sequence || []).filter(Boolean);
    const isSameSequence =
      currentSequence.length === pattern.sequence.length &&
      currentSequence.every((value, index) => value === pattern.sequence[index]);

    return (
      (values.name || "").trim() !== (pattern.name || "").trim() ||
      (values.multiplier || DEFAULT_MULTIPLIER) !==
      (pattern.multiplier || DEFAULT_MULTIPLIER) ||
      values.isActive !== pattern.isActive ||
      !isSameSequence
    );
  };

  const handleCreate = async (values) => {
    const sequence = getSequenceFromValues(values);

    if (sequence.length === 0) {
      messageApi.open({
        type: "error",
        content: "Add at least one position",
      });
      return;
    }

    setSubmitLoading(true);
    try {
      await axios.post(
        `${consts.BackendUrl}/pattern/patterns`,
        {
          role: activeRole,
          name: values.name.trim(),
          status: values.isActive ?? true,
          pattern: toApiPattern(sequence),
          multiplier: Number(values.multiplier),
        },
        {
          headers: authHeaders(),
        },
      );

      form.resetFields();
      form.setFieldsValue({ isActive: true, multiplier: DEFAULT_MULTIPLIER, sequence: [undefined] });
      setIsCreateModalOpen(false);
      messageApi.open({ type: "success", content: "Pattern added successfully" });
      fetchPatterns();
    } catch (error) {
      messageApi.open({
        type: "error",
        content: error.response?.data?.message || "Failed to add pattern",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    form.resetFields();
    form.setFieldsValue({ isActive: true, multiplier: DEFAULT_MULTIPLIER, sequence: [undefined] });
  };

  const handleEdit = (record) => {
    setEditingPattern(record);
    setIsEditPatternChanged(false);
    editForm.setFieldsValue({
      name: record.name,
      multiplier: String(record.multiplier ?? DEFAULT_MULTIPLIER),
      isActive: record.isActive,
      sequence: record.sequence.length > 0 ? record.sequence : [undefined],
    });
  };

  const handleEditValuesChange = (_, allValues) => {
    setIsEditPatternChanged(hasPatternChanged(allValues, editingPattern));
  };

  const handleCloseEditModal = () => {
    setEditingPattern(null);
    setIsEditPatternChanged(false);
  };

  const handleUpdate = async (values) => {
    const sequence = getSequenceFromValues(values);

    if (sequence.length === 0) {
      messageApi.open({
        type: "error",
        content: "Add at least one position",
      });
      return;
    }

    setSubmitLoading(true);
    try {
      await axios.put(
        `${consts.BackendUrl}/pattern/patterns/${getPatternId(editingPattern)}`,
        {
          role: activeRole,
          name: values.name.trim(),
          status: values.isActive,
          pattern: toApiPattern(sequence),
          multiplier: Number(values.multiplier),
        },
        {
          headers: authHeaders(),
        },
      );

      handleCloseEditModal();
      messageApi.open({ type: "success", content: "Pattern updated successfully" });
      fetchPatterns();
    } catch (error) {
      messageApi.open({
        type: "error",
        content: error.response?.data?.message || "Failed to update pattern",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setSubmitLoading(true);
    try {
      await axios.delete(`${consts.BackendUrl}/pattern/patterns/${id}`, {
        headers: authHeaders(),
      });

      setDeletingPattern(null);
      messageApi.open({ type: "success", content: "Pattern deleted successfully" });
      fetchPatterns();
    } catch (error) {
      messageApi.open({
        type: "error",
        content: error.response?.data?.message || "Failed to delete pattern",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRoleChange = (key) => {
    setActiveRole(key);
    setFilterStatus("");
    setPage((prev) => ({ ...prev, current: 1 }));
  };

  const handlePagination = (pagination) => {
    setPage(pagination);
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 70,
      render: (_, __, index) => (page.current - 1) * page.pageSize + index + 1,
    },
    {
      title: "Pattern Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Sequence",
      dataIndex: "sequence",
      key: "sequence",
      render: (sequence) => (
        <Space size={8} className="column-width" wrap>
          {sequence.map((val, index) => (
            <PatternBadge key={index} value={val} />
          ))}
        </Space>
      ),
    },
    {
      title: "Multiplier",
      dataIndex: "multiplier",
      key: "multiplier",
      render: (multiplier) => multiplier || DEFAULT_MULTIPLIER,
    },

    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) =>
        isActive ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (
        <span style={{ whiteSpace: "nowrap" }}>{createdAt}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      render: (_, record) => (
        <Space>
          <Button
            aria-label="Edit pattern"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            aria-label="Delete pattern"
            className="DeleteIco"
            danger
            icon={<DeleteOutlined />}
            onClick={() => setDeletingPattern(record)}
          />
        </Space>
      ),
    },
  ];

  const renderSequenceList = () => (
    <Form.List
      name="sequence"
      rules={[
        {
          validator: async (_, items) => {
            const filled = (items || []).filter(Boolean);
            if (filled.length === 0)
              return Promise.reject(new Error("Add at least one position"));
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <div className="trade-pattern-sequence-dynamic">
          <div className="position-box-grid">
            {fields.map((field, index) => (
              <Form.Item key={field.key} noStyle shouldUpdate>
                {({ getFieldValue, setFieldValue }) => {
                  const listVal = getFieldValue(["sequence", field.name]);
                  return (
                    <div
                      className={`position-box${listVal === "up"
                        ? " position-box--up"
                        : listVal === "down"
                          ? " position-box--down"
                          : ""
                        }`}
                    >
                      <div className="position-box-header">
                        <span className="position-box-label">P{index + 1}</span>
                        <Tooltip title="Remove">
                          <button
                            type="button"
                            className="position-box-remove"
                            onClick={() => remove(field.name)}
                            aria-label={`Remove position ${index + 1}`}
                          >
                            ×
                          </button>
                        </Tooltip>
                      </div>
                      <Form.Item
                        {...field}
                        noStyle
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <div className="position-toggle-group">
                          <button
                            type="button"
                            className={`position-toggle-btn position-toggle-up${listVal === "up" ? " active" : ""
                              }`}
                            onClick={() =>
                              setFieldValue(["sequence", field.name], "up")
                            }
                          >
                            ↑ Up
                          </button>
                          <button
                            type="button"
                            className={`position-toggle-btn position-toggle-down${listVal === "down" ? " active" : ""
                              }`}
                            onClick={() =>
                              setFieldValue(["sequence", field.name], "down")
                            }
                          >
                            ↓ Down
                          </button>
                        </div>
                      </Form.Item>
                    </div>
                  );
                }}
              </Form.Item>
            ))}
            <Tooltip title="Add Position">
              <button
                type="button"
                className="position-box position-box-add"
                onClick={() => add(undefined)}
                aria-label="Add position"
              >
                <PlusOutlined style={{ fontSize: 20 }} />
                <span>Add</span>
              </button>
            </Tooltip>
          </div>
          <Form.ErrorList errors={errors} />
        </div>
      )}
    </Form.List>
  );


  return (
    <>
      <Loadingpage />

      {contextHolder}
      <div className="trade-pattern-page">
        <div className="display-2 p-10 trade-pattern-header">
          <div>
            <h2>Trade Pattern Settings</h2>
            <p>Set Up and Down sequences used by binary trade patterns.</p>
          </div>
          <div className="trade-settings-actions">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              Add Pattern
            </Button>
          </div>
        </div>

        <Tabs
          activeKey={activeRole}
          onChange={handleRoleChange}
          className="custom-tabs"
          items={[
            { key: "basic", label: "Basic Role" },
            { key: "vip", label: "VIP Role" },
            { key: "vvip", label: "VVIP Role" },
          ]}
        />

        <Card className="trade-pattern-info-card" style={{ marginTop: "24px" }}>
          <div className="trade-pattern-info-grid">
            <div>
              <h3>About Patterns</h3>
              <p>Patterns are ordered sequences of Up and Down trade actions for {activeRole.toUpperCase()} role.</p>
              <div className="trade-pattern-example">
                Example:
                <PatternBadge value="up" />
                <PatternBadge value="down" />
                <PatternBadge value="down" />
                <PatternBadge value="up" />
              </div>
            </div>

            <div className="trade-pattern-legend">
              <div>
                <PatternBadge value="up" />
                <span>Up</span>
              </div>
              <div>
                <PatternBadge value="down" />
                <span>Down</span>
              </div>
              <div>
                <PatternBadge />
                <span>Empty/Not set</span>
              </div>
            </div>

            {/* <div className="trade-pattern-limit">
              <span>Length</span>
              <strong>∞</strong>
              <small>unlimited</small>
            </div> */}
          </div>
        </Card>

        <Card className="trade-pattern-table-card" title="Pattern List">
          <div className="trade-settings-actions" style={{ marginBottom: 12 }}>
            <Select
              allowClear
              placeholder="Status"
              value={filterStatus || undefined}
              disabled={loading}
              loading={loading}
              onChange={applyStatusFilter}
              className="trade-settings-filter"
              popupClassName="trade-pattern-select-dropdown"
            >
              <Select.Option value="true">Active</Select.Option>
              <Select.Option value="false">Inactive</Select.Option>
            </Select>
            <Button
              disabled={loading || !filterStatus}
              icon={<CloseCircleOutlined />}
              onClick={clearStatusFilter}
              style={{
                opacity: 1,
                color: "#ff4d4f",
                borderColor: "#ff4d4f",
                background: "transparent",
                cursor: !filterStatus ? "not-allowed" : "pointer",
              }}
            >
              Clear
            </Button>
          </div>
          <EditableTable
            columnsConfig={columns}
            dataSource={patterns}
            total={totalPatterns}
            pageSize={page.pageSize}
            current={page.current}
            closeDisabled={true}
            onChange={handlePagination}
            loading={loading}
          />
        </Card>
      </div>

      <Modal
        title={`Add New Pattern (${activeRole.toUpperCase()})`}
        open={isCreateModalOpen}
        onCancel={handleCloseCreateModal}
        footer={null}
        maskClosable={false}
        centered
        destroyOnHidden
        className="custom-modal trade-pattern-modal"
        maskStyle={{
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        }}
      >
        <Form
          className="trade-pattern-form"
          form={form}
          layout="vertical"
          initialValues={{ isActive: true, multiplier: DEFAULT_MULTIPLIER, sequence: [undefined] }}
          onFinish={handleCreate}
        >
          <div className="trade-pattern-create-grid">
            <Form.Item
              label="Pattern Name"
              name="name"
              rules={[
                { required: true, message: "Enter pattern name" },
                { whitespace: true, message: "Enter pattern name" },
              ]}
            >
              <Input placeholder="Enter pattern name" />
            </Form.Item>

            <Form.Item
              label="Multiplier"
              name="multiplier"
              initialValue={DEFAULT_MULTIPLIER}
              getValueFromEvent={(event) => sanitizeMultiplierInput(event.target.value)}
              rules={multiplierRules}
            >
              <Input inputMode="decimal" placeholder="Enter multiplier" />
            </Form.Item>

            <div className="trade-pattern-sequence-group">
              <label className="trade-pattern-field-label">Positions</label>
              {renderSequenceList()}
            </div>

            <Form.Item label="Status" name="isActive" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </div>

          <div className="trade-pattern-form-actions">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                form.resetFields();
                form.setFieldsValue({ isActive: true, multiplier: DEFAULT_MULTIPLIER, sequence: [undefined] });
              }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={submitLoading}
            >
              Save Pattern
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title={`Edit Pattern (${activeRole.toUpperCase()})`}
        open={Boolean(editingPattern)}
        onCancel={handleCloseEditModal}
        footer={null}
        centered
        destroyOnHidden
        maskClosable={false}

        className="custom-modal trade-pattern-modal"
        maskStyle={{
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        }}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
          onValuesChange={handleEditValuesChange}
        >
          <Form.Item
            label="Pattern Name"
            name="name"
            rules={[
              { required: true, message: "Enter pattern name" },
              { whitespace: true, message: "Enter pattern name" },
            ]}
          >
            <Input placeholder="Enter pattern name" />
          </Form.Item>

          <Form.Item
            label="Multiplier"
            name="multiplier"
            getValueFromEvent={(event) => sanitizeMultiplierInput(event.target.value)}
            rules={multiplierRules}
          >
            <Input inputMode="decimal" placeholder="Enter multiplier" />
          </Form.Item>

          <div className="trade-pattern-sequence-group" style={{ marginBottom: 16 }}>
            <label className="trade-pattern-field-label">Positions</label>
            {renderSequenceList()}
          </div>

          <Form.Item label="Status" name="isActive" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            block
            disabled={!isEditPatternChanged}
            loading={submitLoading}
          >
            Update Pattern
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Delete Pattern"
        open={Boolean(deletingPattern)}
        onCancel={() => setDeletingPattern(null)}
        footer={null}
        maskClosable={false}
        centered
        destroyOnHidden
        className="custom-modal trade-pattern-modal"
        maskStyle={{
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        }}
      >
        <div className="trade-pattern-delete-modal">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deletingPattern?.name}</strong>?
          </p>
          <div className="trade-pattern-form-actions">
            <Button onClick={() => setDeletingPattern(null)}>Cancel</Button>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              loading={submitLoading}
              onClick={() => handleDelete(getPatternId(deletingPattern))}
            >
              Delete Pattern
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TradePatternSettings;
